import { useCallback, useEffect, useRef, useState } from 'react'
import { loadCloudPaymentsScript } from '../api/loadCloudPaymentsScript'
import type { CloudPaymentsWidget } from './types'

type UseCloudPaymentsWidgetOptions = {
    onClose?: () => void
}

export const useCloudPaymentsWidget = ({ onClose }: UseCloudPaymentsWidgetOptions = {}) => {
    const [isWidgetReady, setIsWidgetReady] = useState(false)
    const [widgetError, setWidgetError] = useState<string | null>(null)
    const widgetRef = useRef<CloudPaymentsWidget | null>(null)
    const onCloseRef = useRef(onClose)

    onCloseRef.current = onClose

    const handleWidgetClosed = useCallback(() => {
        onCloseRef.current?.()
        widgetRef.current = null
    }, [])

    const createWidgetInstance = useCallback(() => {
        if (!window.cp?.CloudPayments) return null
        const instance = new window.cp.CloudPayments()
        instance.onclose = handleWidgetClosed

        // Нельзя затирать CP onFrameDestroyed: в нём вызываются onSuccess/onFail
        // (кнопка «Вернуться в магазин»). Только дополняем наш onClose.
        if (instance.containerManager) {
            const originalOnFrameDestroyed = instance.containerManager.onFrameDestroyed
            instance.containerManager.onFrameDestroyed = (...args: unknown[]) => {
                originalOnFrameDestroyed?.(...args)
                handleWidgetClosed()
            }
        }
        return instance
    }, [handleWidgetClosed])

    const closeWidget = useCallback(() => {
        if (!widgetRef.current?.close) return
        widgetRef.current.close()
        widgetRef.current = null
    }, [])

    const getWidgetInstance = useCallback(() => {
        closeWidget()
        const instance = createWidgetInstance()
        if (!instance) return null
        widgetRef.current = instance
        return instance
    }, [closeWidget, createWidgetInstance])

    useEffect(() => {
        if (typeof window === 'undefined') return
        setWidgetError(null)
        loadCloudPaymentsScript()
            .then(() => {
                if (!window.cp?.CloudPayments) {
                    setWidgetError('Платежный виджет не инициализировался.')
                    return
                }
                widgetRef.current = createWidgetInstance()
                setIsWidgetReady(true)
            })
            .catch(() => {
                setWidgetError('Не удалось загрузить платежный виджет.')
            })
    }, [createWidgetInstance])

    return {
        isWidgetReady,
        widgetError,
        getWidgetInstance,
        closeWidget,
    }
}
